import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Code, 
  Megaphone, 
  Shield, 
  Star,
  Clock,
  Users,
  CheckCircle,
  Zap,
  TrendingUp,
  Headphones
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      title: "Website Development",
      description: "Professional websites built for your success, from rapid-launch solutions to comprehensive e-commerce platforms.",
      icon: <Code className="h-8 w-8 text-primary" />,
      link: "/web-development",
      packages: [
        { name: "Foundation", price: "$1,500 - $3,000", description: "Perfect for startups and micro-businesses" },
        { name: "Growth Engine", price: "$5,000 - $12,000", description: "For established businesses needing an upgrade" },
        { name: "Storefront", price: "$7,500 - $20,000+", description: "Complete e-commerce solutions" }
      ],
      features: ["Mobile-responsive design", "SEO optimization", "Client training included", "Fixed-fee pricing"]
    },
    {
      title: "Social Media Marketing",
      description: "Done-for-you social media content that builds your brand and drives business results.",
      icon: <Megaphone className="h-8 w-8 text-accent" />,
      link: "/social-media",
      packages: [
        { name: "The Pulse", price: "$600 - $1,200/mo", description: "2 posts/week on 2 platforms" },
        { name: "The Amplifier", price: "$1,500 - $3,500/mo", description: "4-5 posts/week + video content" }
      ],
      features: ["Professional content creation", "Community management", "Monthly performance reports", "Multi-platform coverage"]
    },
    {
      title: "Maintenance & Support",
      description: "Keep your website secure, fast, and always available with our comprehensive maintenance plans.",
      icon: <Shield className="h-8 w-8 text-primary" />,
      link: "/maintenance",
      packages: [
        { name: "Security Essential", price: "$99 - $150/mo", description: "Complete protection and peace of mind" },
        { name: "Pro Partner", price: "$250 - $500/mo", description: "Full maintenance + content updates" }
      ],
      features: ["24/7 security monitoring", "Automated backups", "Performance optimization", "Emergency support"]
    }
  ];

  const specialOffer = {
    title: "20th Anniversary Special",
    description: "Starter Web Pack for $999",
    subtitle: "Limited to first 10 businesses",
    features: ["3-page professional website", "Mobile-responsive design", "Basic SEO setup", "1 month free support"]
  };

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
            Our Services
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">
            Complete Digital Solutions
            <span className="text-gradient"> for Your Business</span>
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            From professional websites to social media marketing and ongoing maintenance, 
            we provide everything you need to succeed online.
          </p>
        </div>
      </section>

      {/* Special Offer Banner */}
      <section className="py-6 bg-accent text-accent-foreground">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto bg-white/10 border-white/20">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                <div className="text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                    <Star className="h-5 w-5" />
                    <span className="font-heading font-bold text-lg">{specialOffer.title}</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-1">{specialOffer.description}</h3>
                  <p className="font-body text-sm opacity-90">{specialOffer.subtitle}</p>
                </div>
                <div className="flex flex-col items-center space-y-2">
                  <Button asChild variant="secondary" size="lg" className="font-heading font-semibold">
                    <Link to="/booking">Claim This Offer</Link>
                  </Button>
                  <p className="font-body text-xs opacity-75">Limited time offer</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      {/* Main Services */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-6">
              Everything You Need to
              <span className="text-gradient"> Dominate Online</span>
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
              Our comprehensive suite of services covers every aspect of your digital presence, 
              from initial website development to ongoing growth and maintenance.
            </p>
          </div>

          <div className="space-y-12">
            {services.map((service, index) => (
              <Card key={index} className="overflow-hidden shadow-elegant hover-lift">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Service Info */}
                  <div className="p-8 lg:p-12 space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                        {service.icon}
                      </div>
                      <div>
                        <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                          {service.title}
                        </h3>
                      </div>
                    </div>

                    <p className="font-body text-muted-foreground text-lg">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-accent" />
                          <span className="font-body text-sm text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button 
                      asChild 
                      size="lg" 
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
                    >
                      <Link to={service.link}>
                        Learn More About {service.title}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>

                  {/* Packages */}
                  <div className="bg-muted/30 p-8 lg:p-12">
                    <h4 className="font-heading text-lg font-bold text-foreground mb-6">Available Packages</h4>
                    <div className="space-y-4">
                      {service.packages.map((pkg, pkgIndex) => (
                        <Card key={pkgIndex} className="bg-white shadow-sm">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="font-heading font-semibold text-foreground">{pkg.name}</h5>
                              <span className="font-heading text-sm font-bold text-primary">{pkg.price}</span>
                            </div>
                            <p className="font-body text-xs text-muted-foreground">{pkg.description}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Businesses Choose SuccessPro Digital
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              20 years of experience has taught us what truly matters to business owners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <Clock className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Fixed-Fee Pricing</h3>
                <p className="font-body text-sm text-muted-foreground">
                  No hourly creep, no surprise costs. What we quote is what you pay, giving you complete financial control.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <Users className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Client Empowerment</h3>
                <p className="font-body text-sm text-muted-foreground">
                  We don't just build websites; we teach you to manage them. No more developer dependency.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <TrendingUp className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Proven Results</h3>
                <p className="font-body text-sm text-muted-foreground">
                  500+ successful projects and 20 years of experience delivering measurable business results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Ready to Transform Your Digital Presence?
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Let's discuss your goals and create a custom strategy that fits your business needs and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/booking">
                  Schedule Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/case-studies">View Our Results</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;